import { IconStarFilled, IconStar } from "@tabler/icons-react";
import { Loader2 } from "lucide-react";
import { trpc } from "@/lib/trpc";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface GoogleReviewsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  locationId: string;
}

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        star <= rating ? (
          <IconStarFilled key={star} size={14} className="text-amber-400" />
        ) : (
          <IconStar key={star} size={14} className="text-gray-300" />
        )
      ))}
    </div>
  );
}

export function GoogleReviewsModal({
  open,
  onOpenChange,
  locationId,
}: GoogleReviewsModalProps) {
  const { data, isLoading } = trpc.locations.getReviews.useQuery(
    { locationId },
    { enabled: open && !!locationId, staleTime: 5 * 60 * 1000 },
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Google Reviews</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : !data || data.reviews.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">
            No reviews available for this location.
          </p>
        ) : (
          <div className="space-y-4">
            {data.rating != null && (
              <div className="flex items-center gap-2 text-sm">
                <StarRating rating={Math.round(data.rating)} />
                <span className="font-medium">{data.rating}</span>
                <span className="text-muted-foreground">
                  ({data.reviewCount ?? 0} reviews)
                </span>
              </div>
            )}
            <div className="space-y-3">
              {data.reviews.map((review, i) => (
                <div
                  key={i}
                  className="rounded-md border p-3 space-y-1.5"
                >
                  <div className="flex items-center justify-between">
                    <StarRating rating={review.rating} />
                    {review.time && (
                      <span className="text-xs text-muted-foreground">
                        {review.time}
                      </span>
                    )}
                  </div>
                  {review.text && (
                    <p className="text-sm text-foreground">{review.text}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
